import React, { useEffect, useState } from "react";
import { Button, Group, Modal, Radio, Stack, Text } from "@mantine/core";

export interface RemoveReportModalProps {
  /** Display name of the report to remove; null keeps the modal closed. */
  reportName: string | null;
  /** Copies of the report shared with other staff. When > 0 the modal asks
   *  whether to keep their copies or delete them too. */
  shareCount?: number;
  removing: boolean;
  onCancel: () => void;
  /** `deleteSharedCopies`: also remove the copies shared with others. */
  onConfirm: (deleteSharedCopies: boolean) => void;
}

type SharedCopiesChoice = "keep" | "delete";

const copies = (n: number) => (n === 1 ? "1 person" : `${n} people`);

/** "Remove report?" confirmation for taking a report off the user's list. */
export default function RemoveReportModal({
  reportName,
  shareCount = 0,
  removing,
  onCancel,
  onConfirm,
}: RemoveReportModalProps) {
  const opened = reportName !== null;
  const [choice, setChoice] = useState<SharedCopiesChoice>("keep");

  // Every opening starts on the non-destructive choice.
  useEffect(() => {
    if (opened) setChoice("keep");
  }, [opened]);

  return (
    <Modal
      opened={opened}
      onClose={() => {
        if (!removing) onCancel();
      }}
      title={<Text fw={600}>Remove report?</Text>}
      centered
      withCloseButton={!removing}
    >
      <Stack gap="md">
        <Text size="sm">
          Remove{" "}
          <Text component="span" fw={600}>
            {reportName ?? ""}
          </Text>{" "}
          from your list? Its saved view (date range, filters, columns and sort)
          will be lost. You can add the report again anytime from Add report.
        </Text>
        {shareCount > 0 && (
          <Radio.Group
            label={`You shared this report with ${copies(shareCount)}`}
            value={choice}
            onChange={(v) => setChoice(v as SharedCopiesChoice)}
          >
            <Stack gap="xs" mt="xs">
              <Radio
                value="keep"
                label="Keep their copies"
                description="Their copies stay in their lists; they just stop syncing."
              />
              <Radio
                value="delete"
                label="Delete their copies too"
                description="Removes the report from everyone you shared it with."
              />
            </Stack>
          </Radio.Group>
        )}
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onCancel} disabled={removing}>
            Cancel
          </Button>
          <Button
            color="var(--color-danger)"
            onClick={() => onConfirm(shareCount > 0 && choice === "delete")}
            loading={removing}
          >
            Remove
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
